<template>
  <div class="mt-4">
    <p v-if="step.description" class="mb-4">{{ step.description }}</p>
    <template v-if="step.descriptionHtml">
      <p v-for="(line, id) in step.descriptionHtml" :key="id" class="mb-4">
        <span v-html="line"></span>
      </p>
    </template>
    <div class="d-flex gap-2 flex-column">
      <template v-for="(element, index) in step.elements" :key="index">
        <SoForm
          v-if="
            isInputElement(element) &&
            element.isShown &&
            !element.showBelowActions
          "
          ref="form"
          v-model="elementValidityStates[element.key]"
        >
          <SoStepInputElement
            :model-value="values[element.key] ?? element.initialValue"
            :element="element"
            @update:model-value="inputUpdate(element.key, $event)"
          />
        </SoForm>
        <SoStepElement
          v-else-if="element.isShown && !element.showBelowActions"
          :element="element"
        />
      </template>
    </div>

    <SoAlert
      v-if="actionError"
      class="mt-4"
      type="error"
      :title="actionError"
    />

    <div :class="{ 'mt-12': !isMobile, 'mt-4': isMobile }">
      <span>
        <SoButton
          v-if="!isStartingStep"
          data-testid="flow-action-back"
          title="Back"
          large
          class="mr-2"
          @click="($event) => $emit('backClicked')"
        />
      </span>
      <span v-for="(action, index) in step.actions" :key="index">
        <template
          v-if="
            typeof action.showIf === 'object' ||
            (action.showIf && !action.showIfNot)
          "
        >
          <SoButton
            :data-testid="`flow-action-submit-${index}`"
            :type="action.buttonType"
            :loading="isActionPending[index]"
            :disabled="!isValid || action.disabled"
            :title="action.buttonLabel"
            large
            class="mr-2"
            @click="actionSubmitted(action, index)"
          />
        </template>
      </span>
    </div>

    <div
      class="d-flex gap-2 flex-column"
      :class="{ 'mt-4': isMobile, 'mt-8': !isMobile }"
    >
      <template v-for="(element, index) in step.elements" :key="index">
        <SoStepElement
          v-if="element.showBelowActions && element.isShown"
          :element="element"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoStepInputElement from "./components/SoStepInputElement.vue"
  import SoStepElement from "./components/SoStepElement.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import {
    isInputElement,
    IStep,
    IStepAction,
    IStepInputElement,
  } from "./model/flow.types"

  export default defineComponent({
    components: {
      SoStepInputElement,
      SoStepElement,
      SoButton,
      SoAlert,
      SoForm,
    },
    props: {
      step: {
        type: Object as PropType<IStep>,
        required: true,
      },
      values: {
        type: Object,
        required: true,
      },
      isActionPending: {
        type: Object,
        required: true,
      },
      actionError: {
        type: String,
        required: true,
      },
      isStartingStep: {
        type: Boolean,
        required: false,
        default: true,
      },
    },
    emits: {
      input(payload: { key: string; value: unknown }) {
        return !!payload.key
      },
      actionSubmitted(payload: { action: IStepAction; actionIndex: number }) {
        return !!payload.action
      },
      backClicked() {
        return true
      },
    },
    data() {
      return {
        elementValidityStates: {} as Record<string, boolean>,
        isInputElement,
      }
    },
    computed: {
      isMobile() {
        return this.$vuetify.display.mobile
      },
      inputElements() {
        return this.step.elements
          ? (this.step.elements.filter(
              (element) => isInputElement(element) && !!element.isShown,
            ) as IStepInputElement[])
          : []
      },
      requiredInputs() {
        return this.inputElements.filter((element) => !element.optional)
      },
      isValid() {
        return this.requiredInputs.every(
          (v) => !!this.elementValidityStates[v.key],
        )
      },
    },
    methods: {
      inputUpdate(key: string, value) {
        this.$emit("input", { key, value })
        this.requiredInputs.forEach(async (element) => {
          const index = this.inputElements.findIndex(
            (n) => n.key === element.key,
          )
          const forms = this.$refs.form as InstanceType<typeof SoForm>[]

          await this.$nextTick()

          if (!forms) return

          const { valid } = await forms[index].validate()
          this.elementValidityStates[element.key] = valid
        })
      },
      async actionSubmitted(action: IStepAction, actionIndex: number) {
        if (action.to) {
          if (typeof action.to === "object") {
            this.$router.push(action.to)
          } else if (typeof action.to === "string") {
            this.$router.push({
              name: action.to,
            })
          }

          return
        }

        this.$emit("actionSubmitted", { action, actionIndex })
      },
    },
  })
</script>
